import {
  AuthenticationException,
  ClientException,
  ClientExceptionTypes,
  MtException,
} from "@/types/client-exceptions"
import { SimpleHttpError, SimpleHttpResponse } from "@/types/http"
import { BaseQueryFn } from "@reduxjs/toolkit/query"
import { AxiosRequestConfig, CanceledError } from "axios"

import { containsAuthPath } from "@/lib/auth-utils"
import { buildClientAxios } from "@/lib/client-axios"
import { errorMessage, normalizeServerError } from "@/lib/exception-utils"
import { isClientSide } from "@/lib/is-client-side"
import { mtLocation } from "@/lib/mt-location"
import { SocketManager } from "@/lib/socket-manager"

/**
 * Serializable error type for RTK Query state.
 * RTK Query stores errors in Redux state, so they must be plain objects.
 */
export interface SerializedHttpError {
  type: "SimpleHttpError"
  message: string
  response?: SimpleHttpResponse
  requestPath?: string
  requestMethod?: string
  isCancelled?: boolean
  status?: number
}

/**
 * Serializable error type for ClientException errors.
 * Used for domain-specific errors like ConfirmationRequired, ForbiddenException, etc.
 */
export interface SerializedClientError<TData = unknown> {
  type: "ClientException"
  name: ClientExceptionTypes
  message: string
  data?: TData
}

/**
 * Serializable error type for general MtException errors.
 */
export interface SerializedMtError {
  type: "MtException"
  name: string
  message: string
}

export type SerializedError =
  | SerializedHttpError
  | SerializedClientError
  | SerializedMtError

/**
 * Convert an error to a serializable plain object for RTK Query.
 * RTK Query stores errors in Redux state, so they must be plain objects (not class instances).
 */
function serializeError(error: unknown): SerializedError | unknown {
  if (error instanceof SimpleHttpError) {
    return {
      type: "SimpleHttpError" as const,
      message: error.message,
      response: error.response,
      requestPath: error.requestPath,
      requestMethod: error.requestMethod,
      isCancelled: error.isCancelled,
      status: error.response?.status,
    }
  }

  if (error instanceof ClientException) {
    return {
      type: "ClientException" as const,
      name: error.__error_type,
      message: error.message,
      data: error.data,
    }
  }

  if (error instanceof MtException) {
    return {
      type: "MtException" as const,
      name: error.name,
      message: error.message,
    }
  }

  // For other error types, return as-is (they may already be serializable)
  return error
}

/**
 * Type guard to check if an error is a serialized SimpleHttpError with a specific status.
 * Use this instead of instanceof checks when handling RTK Query errors.
 *
 * @example
 * if (isSerializedHttpError(error, 404)) {
 *   // already deleted, nothing to do
 * }
 */
export function isSerializedHttpError(
  error: unknown,
  status?: number
): error is SerializedHttpError {
  return (
    typeof error === "object" &&
    error !== null &&
    "type" in error &&
    error.type === "SimpleHttpError" &&
    (status === undefined || ("status" in error && error.status === status))
  )
}

/**
 * Type guard to check if an error is a serialized ClientException of a specific type.
 * Use this instead of instanceof checks when handling RTK Query errors.
 *
 * @example
 * if (isSerializedClientError(error, "ConfirmationRequired")) {
 *   // error.data is typed correctly
 * }
 */
export function isSerializedClientError<T extends ClientExceptionTypes>(
  error: unknown,
  name: T
): error is SerializedClientError {
  return (
    typeof error === "object" &&
    error !== null &&
    "type" in error &&
    error.type === "ClientException" &&
    "name" in error &&
    error.name === name
  )
}

/**
 * Wait for the WebSocket connection to be established.
 * This is needed for streaming responses that arrive via WebSocket.
 */
export async function waitForSocketConnection(timeoutMs = 5000): Promise<void> {
  if (!SocketManager.hasInstance) {
    return
  }

  const socket = SocketManager.instance.socket
  if (socket.connected) {
    return
  }

  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      socket.off("connect", onConnect)
      reject(new Error("Socket connection timeout"))
    }, timeoutMs)

    const onConnect = () => {
      clearTimeout(timeout)
      socket.off("connect", onConnect)
      resolve()
    }

    socket.on("connect", onConnect)

    // If socket is not even trying to connect, trigger a check
    if (!socket.active) {
      void SocketManager.check()
    }
  })
}

// The slice of axios the base query uses, narrow enough for tests to fake.
export interface RequestClient {
  request(config: AxiosRequestConfig): Promise<{ data: unknown }>
}

/**
 * RTK Query base query that uses our existing axios setup.
 * This ensures we get all the auth handling, error normalization, and other
 * features from our BaseApi class.
 */
export const axiosBaseQuery =
  (
    baseAxios?: RequestClient
  ): BaseQueryFn<
    {
      url: string
      method?: AxiosRequestConfig["method"]
      data?: AxiosRequestConfig["data"]
      params?: AxiosRequestConfig["params"]
      headers?: AxiosRequestConfig["headers"]
      timeout?: AxiosRequestConfig["timeout"]
      signal?: AbortSignal
      skipAuthHandling?: boolean
      retries?: number
      // Resolve the impression token before auth and request handling, just
      // as the endpoints that previously prepared this header did.
      impressionIdHeader?: "Impression-Id" | "impression-id"
    },
    unknown,
    unknown
  > =>
  async (
    {
      url,
      method = "get",
      data,
      params,
      headers,
      timeout,
      signal: requestSignal,
      skipAuthHandling,
      retries,
      impressionIdHeader,
    },
    queryApi
  ) => {
    if (impressionIdHeader) {
      // Lazy import avoids pulling the impressions component into the
      // storage module's initialization cycle.
      const { fetchImpressionId } = await import("@/components/impressions")
      headers = {
        ...headers,
        [impressionIdHeader]: await fetchImpressionId(),
      }
    }

    // RTK aborts queries (unsubscribe, .abort()) through BaseQueryApi's
    // signal; the rare caller-supplied request signal takes over entirely
    // when present.
    const signal = requestSignal ?? queryApi.signal
    const axios = baseAxios ?? buildClientAxios()

    // Lazy import to avoid circular dependency
    const { refreshTokenIfNeeded, refreshAuthToken } =
      await import("@/lib/auth/client")

    const executeRequest = async () => {
      const config: AxiosRequestConfig = {
        method,
        url,
        data,
        params,
        headers,
        timeout,
        signal,
        ...(retries !== undefined && { "axios-retry": { retries } }),
      }

      const response = await axios.request(config)

      // Handle streaming responses (similar to BaseApi.handleResponseData)
      if (
        response.data &&
        typeof response.data === "object" &&
        "streaming_response_id" in response.data
      ) {
        const streamingResponseId = (
          response.data as { streaming_response_id: string }
        ).streaming_response_id

        const streamingData = await new Promise((resolve, reject) => {
          if (signal?.addEventListener) {
            // This is to support axios's cancellation with our custom slow response requests
            signal.addEventListener("abort", () => {
              reject(new CanceledError("RequestAborted"))
            })
          }

          let socketManager: SocketManager
          try {
            socketManager = SocketManager.instance
          } catch (e) {
            throw new Error(
              `SocketManager instance not available for path ${url}: ${errorMessage(e)}`
            )
          }
          socketManager
            // The signal also withdraws the buffer wait, so the fetcher
            // stops polling for a response nobody will consume.
            .waitForResponse(streamingResponseId, signal)
            .then(resolve, reject)
        })

        return { data: streamingData }
      }

      return { data: response.data }
    }

    try {
      // Refresh token if needed before making the request (matches BaseApi behavior)
      if (isClientSide() && !skipAuthHandling) {
        await refreshTokenIfNeeded()
        void SocketManager.check()
      }

      return await executeRequest()
    } catch (error) {
      const normalizedError = normalizeServerError(error, { requestPath: url })

      // Retry once if auth failed (matches BaseApi behavior)
      if (
        isClientSide() &&
        normalizedError instanceof AuthenticationException &&
        !skipAuthHandling
      ) {
        // Pass current URL so failed refresh redirects back (unless it's an auth page)
        const currentUrl = mtLocation.href
        const errorCallbackUrl = containsAuthPath(currentUrl) ? "/" : currentUrl
        await refreshAuthToken(errorCallbackUrl)
        return await executeRequest()
      }

      return { error: serializeError(normalizedError) }
    }
  }
